import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Modal, 
  TextField, 
  Stack, 
  Alert, 
  Tabs, 
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Card,
  CardContent
} from "@mui/material";
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Star, StarBorder, PushPin, PushPinOutlined } from '@mui/icons-material';
import API_BASE_URL from "../apiConfig";
import AdminClubRequestsTable from "../components/AdminClubRequestsTable";
import { PRIMARY_PINK, PRIMARY_PINK_DARK } from "../constants";
import { useTheme } from "../ThemeContext";
import { getDataGridStyles } from "../styles/dataGridStyles";

function AdminConsolePage() {
  const { darkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", handle: "", description: "", avatar_url: "", owner_id: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  
  // All Events and Majors state (for Events tab)
  const [allEvents, setAllEvents] = useState([]);
  const [allMajors, setAllMajors] = useState([]);
  const [allEventsLoading, setAllEventsLoading] = useState(true);
  const [allEventsError, setAllEventsError] = useState("");
  const [allEventsSuccess, setAllEventsSuccess] = useState("");
  
  // Featured Events state (for old featured_events table)
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState("");
  const [eventsSuccess, setEventsSuccess] = useState("");
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventFormData, setEventFormData] = useState({
    title: "",
    content: "",
    image_url: "",
    active: true,
    start_date: "",
    end_date: "",
    priority: 5
  });
  
  // Dialog state for promoting events
  const [openPromoteDialog, setOpenPromoteDialog] = useState(false);
  const [promotingEvent, setPromotingEvent] = useState(null);
  const [promotePriority, setPromotePriority] = useState(5);

  // Calendar Events state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarEventsLoading, setCalendarEventsLoading] = useState(true);
  const [calendarEventsError, setCalendarEventsError] = useState("");
  const [calendarEventsSuccess, setCalendarEventsSuccess] = useState("");

  // Get shared DataGrid styles
  const dataGridStyles = getDataGridStyles(darkMode);

  const handleOpen = () => { setOpen(true); setError(""); setSuccess(""); };
  const handleClose = () => { setOpen(false); setForm({ name: "", handle: "", description: "", avatar_url: "", owner_id: "" }); setError(""); setSuccess(""); };

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 2) { // Events tab
      fetchAllEvents();
      fetchAllMajors();
    } else if (activeTab === 3) { // Featured Events tab
      fetchEvents();
    } else if (activeTab === 4) { // Calendar Events tab
      fetchCalendarEvents();
    }
  }, [activeTab]);

  // Fetch all upcoming events for Events tab
  const fetchAllEvents = async () => {
    try {
      setAllEventsLoading(true);
      setAllEventsError("");
      const response = await fetch(`${API_BASE_URL}/api/events`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const eventsData = await response.json();
        setAllEvents(eventsData);
      } else {
        setAllEventsError("Failed to fetch events");
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setAllEventsError("Failed to fetch events");
    } finally {
      setAllEventsLoading(false);
    }
  };

  // Fetch all upcoming majors for Events tab
  const fetchAllMajors = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/majors`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const majorsData = await response.json();
        setAllMajors(majorsData);
      } else {
        console.error("Failed to fetch majors");
      }
    } catch (error) {
      console.error("Error fetching majors:", error);
    }
  };

  // Fetch featured events for Featured Events tab (updated to show featured events from events table)
  const fetchEvents = async () => {
    try {
      setEventsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/events/featured`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const eventsData = await response.json();
        setEvents(eventsData);
      } else {
        setEventsError("Failed to fetch featured events");
      }
    } catch (error) {
      console.error("Error fetching featured events:", error);
      setEventsError("Failed to fetch featured events");
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchCalendarEvents = async () => {
    try {
      setCalendarEventsLoading(true);
      setCalendarEventsError("");
      const response = await fetch(`${API_BASE_URL}/api/events/calendar`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const eventsData = await response.json();
        setCalendarEvents(Array.isArray(eventsData) ? eventsData : []);
      } else {
        setCalendarEventsError("Failed to fetch calendar events");
      }
    } catch (error) {
      console.error("Error fetching calendar events:", error);
      setCalendarEventsError("Failed to fetch calendar events");
    } finally {
      setCalendarEventsLoading(false);
    }
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    setEventsError("");
    setEventsSuccess("");

    try {
      const url = editingEvent 
        ? `${API_BASE_URL}/api/featured/${editingEvent.id}`
        : `${API_BASE_URL}/api/featured`;
      
      const method = editingEvent ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(eventFormData),
      });

      if (response.ok) {
        const result = await response.json();
        setEventsSuccess(editingEvent ? "Featured event updated successfully!" : "Featured event created successfully!");
        setOpenEventDialog(false);
        resetEventForm();
        fetchEvents();
      } else {
        const errorData = await response.json();
        setEventsError(errorData.error || "Failed to save featured event");
      }
    } catch (error) {
      console.error("Error saving featured event:", error);
      setEventsError("Failed to save featured event");
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventFormData({
      title: event.title || "",
      content: event.content || "",
      image_url: event.image_url || "",
      active: event.active !== undefined ? event.active : true,
      start_date: event.start_date ? event.start_date.split('T')[0] : "",
      end_date: event.end_date ? event.end_date.split('T')[0] : "",
      priority: event.priority || 5
    });
    setOpenEventDialog(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this featured event?")) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/featured/${eventId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setEventsSuccess("Featured event deleted successfully!");
        fetchEvents();
      } else {
        const errorData = await response.json();
        setEventsError(errorData.error || "Failed to delete featured event");
      }
    } catch (error) {
      console.error("Error deleting featured event:", error);
      setEventsError("Failed to delete featured event");
    }
  };

  const resetEventForm = () => {
    setEditingEvent(null);
    setEventFormData({
      title: "",
      content: "",
      image_url: "",
      active: true,
      start_date: "",
      end_date: "",
      priority: 5
    });
  };

  const handleOpenEventDialog = () => {
    resetEventForm();
    setOpenEventDialog(true);
  };

  const handleCloseEventDialog = () => {
    setOpenEventDialog(false);
    resetEventForm();
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Promote/Demote functions for Events tab
  const handleOpenPromoteDialog = (event) => {
    setPromotingEvent(event);
    setPromotePriority(event.priority || 5);
    setOpenPromoteDialog(true);
  };

  const handleClosePromoteDialog = () => {
    setOpenPromoteDialog(false);
    setPromotingEvent(null);
    setPromotePriority(5);
  };

  const handlePromoteEvent = async () => {
    if (!promotingEvent) return;

    const isMajor = promotingEvent.item_type === 'major' || promotingEvent.start_date !== undefined;
    const endpoint = isMajor 
      ? `${API_BASE_URL}/api/majors/${promotingEvent.id}/featured`
      : `${API_BASE_URL}/api/events/${promotingEvent.id}/featured`;

    try {
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          featured: true,
          priority: promotePriority
        }),
      });

      if (response.ok) {
        const itemType = isMajor ? 'Major' : 'Event';
        setAllEventsSuccess(`${itemType} "${promotingEvent.title}" promoted to featured!`);
        fetchAllEvents();
        if (isMajor) {
          fetchAllMajors();
        }
        handleClosePromoteDialog();
      } else {
        const errorData = await response.json();
        setAllEventsError(errorData.error || "Failed to promote");
      }
    } catch (error) {
      console.error("Error promoting:", error);
      setAllEventsError("Failed to promote");
    }
  };

  const handleDemoteEvent = async (item) => {
    const isMajor = item.item_type === 'major' || item.start_date !== undefined;
    const itemType = isMajor ? 'Major' : 'Event';
    
    if (!window.confirm(`Remove "${item.title}" from featured ${itemType.toLowerCase()}s?`)) {
      return;
    }

    const endpoint = isMajor 
      ? `${API_BASE_URL}/api/majors/${item.id}/featured`
      : `${API_BASE_URL}/api/events/${item.id}/featured`;

    try {
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          featured: false,
          priority: null
        }),
      });

      if (response.ok) {
        setAllEventsSuccess(`${itemType} "${item.title}" removed from featured!`);
        fetchAllEvents();
        if (isMajor) {
          fetchAllMajors();
        }
      } else {
        const errorData = await response.json();
        setAllEventsError(errorData.error || `Failed to demote ${itemType.toLowerCase()}`);
      }
    } catch (error) {
      console.error(`Error demoting ${itemType.toLowerCase()}:`, error);
      setAllEventsError(`Failed to demote ${itemType.toLowerCase()}`);
    }
  };

  // Promote/Demote functions for Majors
  const handleOpenPromoteMajorDialog = (major) => {
    setPromotingEvent(major);
    setPromotePriority(major.priority || 5);
    setOpenPromoteDialog(true);
  };

  const handlePromoteMajor = async () => {
    if (!promotingEvent) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/majors/${promotingEvent.id}/featured`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          featured: true,
          priority: promotePriority
        }),
      });

      if (response.ok) {
        setAllEventsSuccess(`Major "${promotingEvent.title}" promoted to featured!`);
        fetchAllMajors();
        handleClosePromoteDialog();
      } else {
        const errorData = await response.json();
        setAllEventsError(errorData.error || "Failed to promote major");
      }
    } catch (error) {
      console.error("Error promoting major:", error);
      setAllEventsError("Failed to promote major");
    }
  };

  const handleDemoteMajor = async (major) => {
    if (!window.confirm(`Remove "${major.title}" from featured majors?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/majors/${major.id}/featured`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          featured: false,
          priority: null
        }),
      });

      if (response.ok) {
        setAllEventsSuccess(`Major "${major.title}" removed from featured!`);
        fetchAllMajors();
      } else {
        const errorData = await response.json();
        setAllEventsError(errorData.error || "Failed to demote major");
      }
    } catch (error) {
      console.error("Error demoting major:", error);
      setAllEventsError("Failed to demote major");
    }
  };

  const isMajorItem = (item) => item?.item_type === 'major' || (item?.start_date !== undefined && item?.start_time === undefined);

  const handlePinEvent = async (item) => {
    if (!item) return;
    const isMajor = isMajorItem(item);
    const itemType = isMajor ? 'Major' : 'Event';
    const endpoint = isMajor
      ? `${API_BASE_URL}/api/majors/${item.id}/calendar`
      : `${API_BASE_URL}/api/events/${item.id}/calendar`;

    try {
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          calendar_pinned: true
        }),
      });

      if (response.ok) {
        setAllEventsSuccess(`${itemType} "${item.title}" pinned to the calendar!`);
        setCalendarEventsSuccess(`${itemType} "${item.title}" pinned to the calendar!`);
        fetchAllEvents();
        if (isMajor) {
          fetchAllMajors();
        }
        fetchCalendarEvents();
      } else {
        const errorData = await response.json();
        setAllEventsError(errorData.error || `Failed to pin ${itemType.toLowerCase()}`);
        setCalendarEventsError(errorData.error || `Failed to pin ${itemType.toLowerCase()}`);
      }
    } catch (error) {
      console.error("Error pinning item:", error);
      setAllEventsError(`Failed to pin ${itemType.toLowerCase()}`);
      setCalendarEventsError(`Failed to pin ${itemType.toLowerCase()}`);
    }
  };

  const handleUnpinEvent = async (item) => {
    if (!item) return;
    const isMajor = isMajorItem(item);
    const itemType = isMajor ? 'Major' : 'Event';

    if (!window.confirm(`Remove "${item.title}" from the monthly calendar?`)) {
      return;
    }

    const endpoint = isMajor
      ? `${API_BASE_URL}/api/majors/${item.id}/calendar`
      : `${API_BASE_URL}/api/events/${item.id}/calendar`;

    try {
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          calendar_pinned: false
        }),
      });

      if (response.ok) {
        setAllEventsSuccess(`${itemType} "${item.title}" removed from the calendar!`);
        setCalendarEventsSuccess(`${itemType} "${item.title}" removed from the calendar!`);
        fetchAllEvents();
        if (isMajor) {
          fetchAllMajors();
        }
        fetchCalendarEvents();
      } else {
        const errorData = await response.json();
        setAllEventsError(errorData.error || `Failed to unpin ${itemType.toLowerCase()}`);
        setCalendarEventsError(errorData.error || `Failed to unpin ${itemType.toLowerCase()}`);
      }
    } catch (error) {
      console.error("Error unpinning item:", error);
      setAllEventsError(`Failed to unpin ${itemType.toLowerCase()}`);
      setCalendarEventsError(`Failed to unpin ${itemType.toLowerCase()}`);
    }
  };

  // Define DataGrid columns for all events and majors (Events tab)
  const allEventsColumns = [
    {
      field: 'item_type',
      headerName: 'Type',
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => {
        const isMajor = params.row.item_type === 'major' || params.row.start_date !== undefined;
        return (
          <Chip
            label={isMajor ? 'Major' : 'Event'}
            color={isMajor ? 'secondary' : 'primary'}
            size="small"
          />
        );
      }
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="subtitle2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'club_name',
      headerName: 'Club',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => {
        return row.start_time || row.start_date;
      },
      renderCell: (params) => {
        const dateValue = params.row.start_time || params.row.start_date;
        if (!dateValue) return null;
        const isMajor = params.row.item_type === 'major' || params.row.start_date !== undefined;
        const date = new Date(dateValue);
        if (isMajor) {
          return (
            <Typography variant="body2" fontSize="0.85rem">
              {date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </Typography>
          );
        }
        return (
          <Typography variant="body2" fontSize="0.85rem">
            {date.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        );
      }
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        if (!params.value) return null;
        return (
          <Typography variant="body2" fontSize="0.8rem" sx={{
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {params.value}
          </Typography>
        );
      }
    },
    {
      field: 'featured',
      headerName: 'Featured',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          color={params.value ? 'success' : 'default'}
          size="small"
          icon={params.value ? <Star /> : undefined}
        />
      )
    },
    {
      field: 'calendar_pinned',
      headerName: 'Calendar',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Pinned' : 'No'}
          color={params.value ? 'success' : 'default'}
          size="small"
          icon={params.value ? <PushPin /> : undefined}
        />
      )
    },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => (
        params.value !== null && params.value !== undefined ? (
          <Chip
            label={params.value}
            color="primary"
            size="small"
          />
        ) : null
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {params.row.featured ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleDemoteEvent(params.row)}
                startIcon={<StarBorder />}
                sx={{ 
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  color: '#f44336',
                  borderColor: '#f44336',
                  '&:hover': {
                    borderColor: '#d32f2f',
                    backgroundColor: 'rgba(244, 67, 54, 0.08)'
                  }
                }}
              >
                Demote
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleOpenPromoteDialog(params.row)}
                startIcon={<Star />}
                sx={{ 
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  color: PRIMARY_PINK,
                  borderColor: PRIMARY_PINK,
                  '&:hover': {
                    borderColor: PRIMARY_PINK_DARK,
                    backgroundColor: 'rgba(233, 30, 99, 0.08)'
                  }
                }}
              >
                Promote
              </Button>
            )}
            {params.row.calendar_pinned ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleUnpinEvent(params.row)}
                startIcon={<PushPinOutlined />}
                sx={{ 
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  color: '#f44336',
                  borderColor: '#f44336',
                  '&:hover': {
                    borderColor: '#d32f2f',
                    backgroundColor: 'rgba(244, 67, 54, 0.08)'
                  }
                }}
              >
                Unpin
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handlePinEvent(params.row)}
                startIcon={<PushPin />}
                sx={{ 
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  color: PRIMARY_PINK,
                  borderColor: PRIMARY_PINK,
                  '&:hover': {
                    borderColor: PRIMARY_PINK_DARK,
                    backgroundColor: 'rgba(233, 30, 99, 0.08)'
                  }
                }}
              >
                Pin
              </Button>
            )}
          </Box>
        );
      }
    }
  ];

  // Define DataGrid columns for featured events and majors
  const eventColumns = [
    {
      field: 'item_type',
      headerName: 'Type',
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => {
        const isMajor = params.row.item_type === 'major' || params.row.start_date !== undefined;
        return (
          <Chip
            label={isMajor ? 'Major' : 'Event'}
            color={isMajor ? 'secondary' : 'primary'}
            size="small"
          />
        );
      }
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="subtitle2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'club_name',
      headerName: 'Club',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => {
        return row.start_time || row.start_date;
      },
      renderCell: (params) => {
        const dateValue = params.row.start_time || params.row.start_date;
        if (!dateValue) return null;
        const isMajor = params.row.item_type === 'major' || params.row.start_date !== undefined;
        const date = new Date(dateValue);
        if (isMajor) {
          return (
            <Typography variant="body2" fontSize="0.85rem">
              {date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </Typography>
          );
        }
        return (
          <Typography variant="body2" fontSize="0.85rem">
            {date.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        );
      }
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        if (!params.value) return null;
        return (
          <Typography variant="body2" fontSize="0.8rem" sx={{
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {params.value}
          </Typography>
        );
      }
    },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          color="primary"
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const isMajor = params.row.item_type === 'major' || params.row.start_date !== undefined;
              if (isMajor) {
                handleDemoteMajor(params.row);
              } else {
                handleDemoteEvent(params.row);
              }
            }}
            startIcon={<StarBorder />}
            sx={{ 
              fontSize: '0.7rem',
              padding: '2px 8px',
              color: '#f44336',
              borderColor: '#f44336',
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: 'rgba(244, 67, 54, 0.08)'
              }
            }}
          >
            Demote
          </Button>
        </Box>
      )
    }
  ];

  const calendarColumns = [
    {
      field: 'item_type',
      headerName: 'Type',
      flex: 0.4,
      minWidth: 80,
      renderCell: (params) => {
        const isMajor = isMajorItem(params.row);
        return (
          <Chip
            label={isMajor ? 'Major' : 'Event'}
            color={isMajor ? 'secondary' : 'primary'}
            size="small"
          />
        );
      }
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="subtitle2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'club_name',
      headerName: 'Club',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => row.start_time || row.start_date,
      renderCell: (params) => {
        const dateValue = params.row.start_time || params.row.start_date;
        if (!dateValue) return null;
        const isMajor = isMajorItem(params.row);
        const date = new Date(dateValue);
        if (isMajor) {
          return (
            <Typography variant="body2" fontSize="0.85rem">
              {date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </Typography>
          );
        }
        return (
          <Typography variant="body2" fontSize="0.85rem">
            {date.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        );
      }
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        if (!params.value) return null;
        return (
          <Typography variant="body2" fontSize="0.8rem" sx={{
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {params.value}
          </Typography>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleUnpinEvent(params.row)}
            startIcon={<PushPinOutlined />}
            sx={{ 
              fontSize: '0.7rem',
              padding: '2px 8px',
              color: '#f44336',
              borderColor: '#f44336',
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: 'rgba(244, 67, 54, 0.08)'
              }
            }}
          >
            Unpin
          </Button>
        </Box>
      )
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to create club");
      }
      setSuccess("Club created successfully!");
      setForm({ name: "", handle: "", description: "", avatar_url: "", owner_id: "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Paper sx={{ p: 4, mb: 3 }}>
        <Typography variant="h3" fontWeight="bold" mb={2}>Admin Console</Typography>
        <Typography variant="body1" mb={2}>Welcome, admin! Here you can manage users, clubs, and more.</Typography>
      </Paper>

      <Paper sx={{ p: 4 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Create Club" />
          <Tab label="New Club Requests" />
          <Tab label="Events" />
          <Tab label="Featured Events" />
          <Tab label="Calendar Events" />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            <Button 
              variant="contained" 
              onClick={handleOpen} 
              sx={{ 
                mb: 2,
                bgcolor: PRIMARY_PINK,
                color: '#ffffff',
                '&:hover': {
                  bgcolor: PRIMARY_PINK_DARK
                }
              }}
            >
              Create Club
            </Button>
          </Box>
        )}

        {activeTab === 1 && (
          <AdminClubRequestsTable />
        )}

        {activeTab === 2 && (
          <Box>
            {/* Success/Error Messages */}
            {allEventsSuccess && <Alert severity="success" sx={{ mb: 2 }}>{allEventsSuccess}</Alert>}
            {allEventsError && <Alert severity="error" sx={{ mb: 2 }}>{allEventsError}</Alert>}

            {/* Events and Majors Table */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: PRIMARY_PINK }}>
                  All Upcoming Events & Majors
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Promote events and majors to feature them on the homepage, or pin them to the public monthly calendar. Featured priority is 1-10 (higher numbers appear first).
                </Typography>
                
                <Box sx={{ height: 600, width: '100%', ...dataGridStyles }}>
                  <DataGrid
                    rows={[
                      ...allEvents.map(e => ({ ...e, item_type: 'event' })),
                      ...allMajors.map(m => ({ ...m, item_type: 'major' }))
                    ]}
                    columns={allEventsColumns}
                    getRowId={(row) => `${row.item_type}-${row.id}`}
                    loading={allEventsLoading}
                    pageSize={10}
                    rowsPerPageOptions={[5, 10, 25]}
                    disableSelectionOnClick
                    rowHeight={72}
                    sx={dataGridStyles}
                    componentsProps={{
                      filterPanel: {
                        sx: {
                          backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
                          color: darkMode ? '#ffffff' : '#000000',
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {activeTab === 3 && (
          <Box>
            {/* Success/Error Messages */}
            {eventsSuccess && <Alert severity="success" sx={{ mb: 2 }}>{eventsSuccess}</Alert>}
            {eventsError && <Alert severity="error" sx={{ mb: 2 }}>{eventsError}</Alert>}

            {/* Events Table */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: PRIMARY_PINK }}>
                  Currently Featured Events & Majors
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Events and majors with featured=true that are displayed on the homepage. Use the Events tab to promote or demote items.
                </Typography>
                
                <Box sx={{ height: 600, width: '100%', ...dataGridStyles }}>
                  <DataGrid
                    rows={events}
                    columns={eventColumns}
                    getRowId={(row) => row.id}
                    loading={eventsLoading}
                    pageSize={10}
                    rowsPerPageOptions={[5, 10, 25]}
                    disableSelectionOnClick
                    sx={dataGridStyles}
                    componentsProps={{
                      filterPanel: {
                        sx: {
                          backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
                          color: darkMode ? '#ffffff' : '#000000',
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {activeTab === 4 && (
          <Box>
            {calendarEventsSuccess && <Alert severity="success" sx={{ mb: 2 }}>{calendarEventsSuccess}</Alert>}
            {calendarEventsError && <Alert severity="error" sx={{ mb: 2 }}>{calendarEventsError}</Alert>}

            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: PRIMARY_PINK }}>
                  Currently Pinned Calendar Events & Majors
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Events and majors with calendar_pinned=true that appear on the homepage monthly calendar. Use the Events tab to pin or unpin items.
                </Typography>
                
                <Box sx={{ height: 600, width: '100%', ...dataGridStyles }}>
                  <DataGrid
                    rows={calendarEvents}
                    columns={calendarColumns}
                    getRowId={(row) => `${row.item_type || 'event'}-${row.id}`}
                    loading={calendarEventsLoading}
                    pageSize={10}
                    rowsPerPageOptions={[5, 10, 25]}
                    disableSelectionOnClick
                    sx={dataGridStyles}
                    componentsProps={{
                      filterPanel: {
                        sx: {
                          backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
                          color: darkMode ? '#ffffff' : '#000000',
                        },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Paper>
      <Modal open={open} onClose={handleClose}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 400, bgcolor: "background.paper", boxShadow: 24, p: 4, borderRadius: 2 }}>
          <Typography variant="h5" mb={2}>Create New Club</Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField label="Club Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
              <TextField label="Handle (no @, unique)" name="handle" value={form.handle} onChange={handleChange} required fullWidth />
              <TextField label="Description" name="description" value={form.description} onChange={handleChange} multiline rows={3} fullWidth />
              <TextField label="Avatar URL" name="avatar_url" value={form.avatar_url} onChange={handleChange} fullWidth />
              <TextField label="Owner ID" name="owner_id" value={form.owner_id} onChange={handleChange} required fullWidth />
              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}
              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading}
                sx={{
                  bgcolor: PRIMARY_PINK,
                  color: '#ffffff',
                  '&:hover': {
                    bgcolor: PRIMARY_PINK_DARK
                  }
                }}
              >
                {loading ? "Creating..." : "Create Club"}
              </Button>
              <Button 
                onClick={handleClose}
                sx={{
                  color: '#000000',
                  borderColor: '#000000',
                  '&:hover': {
                    borderColor: '#000000',
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Cancel
              </Button>
            </Stack>
          </form>
        </Box>
      </Modal>

      {/* Featured Events Dialog */}
      <Dialog open={openEventDialog} onClose={handleCloseEventDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div" sx={{ color: 'text.primary' }}>
            {editingEvent ? 'Edit Featured Event' : 'Add New Featured Event'}
          </Typography>
        </DialogTitle>
        
        <Box component="form" onSubmit={handleEventSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Title */}
              <TextField
                fullWidth
                label="Title"
                value={eventFormData.title}
                onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })}
                required
                helperText={`${eventFormData.title.length}/200 characters`}
                error={eventFormData.title.length > 200}
              />

              {/* Content */}
              <TextField
                fullWidth
                label="Content"
                value={eventFormData.content}
                onChange={(e) => setEventFormData({ ...eventFormData, content: e.target.value })}
                required
                multiline
                rows={4}
                helperText={`${eventFormData.content.length}/1000 characters`}
                error={eventFormData.content.length > 1000}
              />

              {/* Image URL */}
              <TextField
                fullWidth
                label="Image URL (optional)"
                value={eventFormData.image_url}
                onChange={(e) => setEventFormData({ ...eventFormData, image_url: e.target.value })}
                helperText="URL to an image for this featured event"
              />

              {/* Active Status */}
              <FormControlLabel
                control={
                  <Switch
                    checked={eventFormData.active}
                    onChange={(e) => setEventFormData({ ...eventFormData, active: e.target.checked })}
                  />
                }
                label="Active"
              />

              {/* Priority */}
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={eventFormData.priority}
                  label="Priority"
                  onChange={(e) => setEventFormData({ ...eventFormData, priority: e.target.value })}
                >
                  <MenuItem value={1}>1 - Highest</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={4}>4</MenuItem>
                  <MenuItem value={5}>5 - Default</MenuItem>
                  <MenuItem value={6}>6</MenuItem>
                  <MenuItem value={7}>7</MenuItem>
                  <MenuItem value={8}>8</MenuItem>
                  <MenuItem value={9}>9</MenuItem>
                  <MenuItem value={10}>10 - Lowest</MenuItem>
                </Select>
              </FormControl>

              {/* Date Range */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Start Date (optional)"
                  type="date"
                  value={eventFormData.start_date}
                  onChange={(e) => setEventFormData({ ...eventFormData, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="End Date (optional)"
                  type="date"
                  value={eventFormData.end_date}
                  onChange={(e) => setEventFormData({ ...eventFormData, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseEventDialog} variant="outlined">
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                bgcolor: PRIMARY_PINK,
                color: '#ffffff',
                '&:hover': {
                  bgcolor: PRIMARY_PINK_DARK
                }
              }}
            >
              {editingEvent ? 'Update Event' : 'Create Event'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Promote Event/Major Dialog */}
      <Dialog open={openPromoteDialog} onClose={handleClosePromoteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div" sx={{ color: 'text.primary' }}>
            {promotingEvent && (promotingEvent.item_type === 'major' || promotingEvent.start_date !== undefined)
              ? 'Promote Major to Featured'
              : 'Promote Event to Featured'}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {promotingEvent && (promotingEvent.item_type === 'major' || promotingEvent.start_date !== undefined)
                ? 'Major'
                : 'Event'}: <strong>{promotingEvent?.title}</strong>
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={promotePriority}
                label="Priority"
                onChange={(e) => setPromotePriority(e.target.value)}
              >
                <MenuItem value={1}>1 - Highest</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
                <MenuItem value={4}>4</MenuItem>
                <MenuItem value={5}>5 - Default</MenuItem>
                <MenuItem value={6}>6</MenuItem>
                <MenuItem value={7}>7</MenuItem>
                <MenuItem value={8}>8</MenuItem>
                <MenuItem value={9}>9</MenuItem>
                <MenuItem value={10}>10 - Lowest</MenuItem>
              </Select>
            </FormControl>
            
            <Typography variant="caption" color="text.secondary">
              Higher priority numbers appear first on the homepage. Featured items are sorted by priority (descending), then by start date/time.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClosePromoteDialog} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handlePromoteEvent} 
            variant="contained"
            startIcon={<Star />}
            sx={{
              bgcolor: PRIMARY_PINK,
              color: '#ffffff',
              '&:hover': {
                bgcolor: PRIMARY_PINK_DARK
              }
            }}
          >
            Promote to Featured
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminConsolePage; 